import { Loader2, Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useApi } from 'shared_components/context';
import { ArtistDiscogImage } from './ArtistDiscography/ArtistDiscogImage';
import Button from 'shared_components/components/Button';
import { useClientId } from 'shared_components/context/client';
import { useDiscographyIds } from './useDiscographyIds';
import { ArtistImageNotFound } from './ArtistDiscography/ArtistImageNotFound';
import { DiscogsSearchArtistsInner } from 'shared_components/generated/admin';
import { ArtistMusicbrainzImage } from './ArtistDiscography/ArtistMusicbrainzImage';
import { RateLimiterProvider } from './ArtistDiscography/RateLimiterProvider';
import WModal from 'shared_components/components/WModal';
import WButton from 'shared_components/components/WForms/WButton/WButton';
import { generateArtistUrlBySource } from './ArtistDiscography/utils';
import { usePrimaryDiscographyRun } from './usePrimaryDiscographyRun';

function NoQueryInformation() {
  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-8 tw-text-center tw-h-64">
      <Search size={16} className="text-gray-400" />
      <p className="tw-text-gray-400 tw-text-sm mb-2">
        Start typing to search for artists, releases, and labels
      </p>
      <p className="tw-text-gray-500 tw-text-xs">
        Results will appear as you type
      </p>
    </div>
  );
}

export function ArtistDiscographyIDSearch() {
  const [searchQuery, setSearchQuery] = useState('');
  const isLoadingMore = false;
  const { clientId, setClientEmail } = useClientId();
  const searchResultsRef = useRef<HTMLDivElement>(null);

  const [confirmationModalDetails, setConfirmationModalDetails] = useState<{
    clientId: string;
    source: string;
    sourceId: string;
  } | null>(null);

  const baseApi = useApi();
  const api = baseApi.adminApi;

  const {
    autoCollectIds,
    status: autoCollectStatus,
    source: autoCollectSource,
    sourceId: autoCollectSourceId,
  } = useDiscographyIds();

  const { actionsDisabled } = usePrimaryDiscographyRun();

  const [displayedResults, setDisplayedResults] = useState<
    (DiscogsSearchArtistsInner & { title: string; type: string })[]
  >([]);

  useEffect(() => {
    // Immediately reset scroll position on new search
    if (searchResultsRef.current) {
      searchResultsRef.current.scrollTop = 0;
    }

    if (searchQuery.trim() === '') {
      setDisplayedResults([]);
      return;
    }

    const debounceTimer = setTimeout(() => {
      api
        .searchDiscogsSearch({
          searchTerm: searchQuery,
        })
        .then((data) => {
          const basicList = data.artists.map((artist) => ({
            title: artist.name,
            type: 'Artist',
            ...artist,
          }));

          const collapsedList = basicList.filter((artist) => {
            const { externalIds } = artist;

            if (!Array.isArray(externalIds)) return true;

            if (artist.source === 'discogs') return true;

            // If there is already an alias from discogs we collapse this result
            if (artist.source === 'musicbrainz') {
              if (
                basicList.find(
                  (originalArtist) =>
                    originalArtist.source === 'discogs' &&
                    externalIds.some(
                      (externalId) =>
                        externalId.id === originalArtist.id &&
                        externalId.source === 'discogs'
                    )
                )
              ) {
                return false;
              }
            }

            return true;
          });

          console.log('collapsedList results', collapsedList);

          setDisplayedResults(collapsedList);
        })
        .catch((error) => console.log(error));
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, api]);

  const handlePopulateIdsClick = (source_id: string, source: string) => {
    if (!clientId) return;

    api
      .retrieveCollectedIdsAutoCollectedIds({ userId: clientId })
      .then((data) => {
        const ids = data.collectedIds;
        if (ids && (!Array.isArray(ids) || ids.length > 0)) {
          setConfirmationModalDetails({ clientId, source, sourceId: source_id });
        } else {
          autoCollectIds(clientId, source_id, source);
        }
      })
      .catch(() => {
        autoCollectIds(clientId, source_id, source);
      });
  };

  return (
    <RateLimiterProvider>
      <div className="tw-h-full tw-w-h-full tw-w-[30%] tw-border-r tw-border-gray-700 tw-flex tw-flex-col tw-relative">
        {/* Search header */}
        <div className="tw-border-b tw-border-gray-700 tw-p-4 ">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
            <h2 className="tw-text-white tw-font-medium tw-text-lg">Search</h2>
          </div>
          <div className="tw-relative tw-w-full">
            <div className="tw-relative">
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="tw-w-full tw-bg-gray-700 tw-border-none tw-rounded-md tw-pl-10 tw-pr-10 tw-py-2 tw-text-white tw-outline-none"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
              <div className="tw-absolute tw-inset-y-0 tw-left-0 tw-pl-3 tw-flex tw-items-center tw-pointer-events-none">
                <Search size={16} className="tw-text-gray-400" />
              </div>
              {searchQuery && (
                <button
                  className="tw-absolute tw-inset-y-0 tw-right-0 tw-pr-3 tw-flex tw-items-center"
                  onClick={() => setSearchQuery('')}
                >
                  <X
                    size={16}
                    className="tw-text-gray-400 tw-hover-text-white"
                  />
                </button>
              )}
            </div>
          </div>
        </div>
        {/* Search results container with overflow */}
        <div className="tw-flex-1 tw-overflow-y-auto" ref={searchResultsRef}>
          {searchQuery ? (
            // Search results when query exists
            <>
              {displayedResults.map((result) => (
                <div
                  key={result.id}
                  className="tw-border-b tw-border-gray-700 tw-py-2 tw-px-3 tw-flex tw-items-center"
                >
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={generateArtistUrlBySource(
                      result.id,
                      result.source as any
                    )}
                    className="tw-w-12 tw-h-12 tw-mr-3 tw-flex-shrink-0 tw-bg-gray-700 tw-rounded tw-overflow-hidden"
                  >
                    {result.source === 'discogs' ? (
                      <ArtistDiscogImage
                        discogId={result.id}
                        title={result.title}
                      />
                    ) : result.source === 'musicbrainz' ? (
                      <ArtistMusicbrainzImage
                        title={result.title}
                        externalIds={result.externalIds ?? []}
                      />
                    ) : (
                      <ArtistImageNotFound />
                    )}
                  </a>
                  <div className="tw-flex-grow tw-min-w-0">
                    <h3 className="tw-text-white tw-font-bold tw-text-sm tw-truncate tw-font-sans">
                      {result.title}
                    </h3>
                    {/* {result.artist && (
                      <p className="tw-text-gray-400 tw-text-xs">
                        {result.artist}
                      </p>
                    )} */}
                    <div className="tw-flex tw-items-center tw-mt-0.5">
                      <span className="tw-text-gray-300 tw-text-xs">
                        {result.type}
                      </span>
                      {/* {result.year && (
                        <>
                          <span className="tw-mx-1 tw-text-gray-500 tw-text-xs">
                            •
                          </span>
                          <span className="tw-text-gray-400 tw-text-xs">
                            {result.year}
                          </span>
                        </>
                      )} */}
                      {result.source && (
                        <>
                          <span className="tw-mx-1 tw-text-gray-500 tw-text-xs">
                            •
                          </span>
                          <span className="tw-text-gray-400 tw-text-xs">
                            {result.source}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  {autoCollectStatus !== 'loading' &&
                  autoCollectStatus !== 'preparing' &&
                  !actionsDisabled ? (
                    <div className="tw-scale-75">
                      <Button
                        styleType="btn-primary"
                        onClick={() =>
                          handlePopulateIdsClick(result.id, result.source)
                        }
                      >
                        Populate IDs
                      </Button>
                    </div>
                  ) : result.id === autoCollectSourceId &&
                    result.source === autoCollectSource &&
                    !actionsDisabled ? (
                    <div className="tw-scale-75 tw-animate-pulse">
                      <Button
                        disabled
                        onClick={() => {}}
                        styleType="btn-primary"
                      >
                        Populating IDs...
                      </Button>
                    </div>
                  ) : null}
                </div>
              ))}
              {isLoadingMore && (
                <div className="tw-flex tw-justify-center tw-items-center tw-py-4">
                  <Loader2
                    size={24}
                    className="tw-text-westburyBlue tw-animate-spin"
                  />
                </div>
              )}
            </>
          ) : (
            // Instructions when no query
            <NoQueryInformation />
          )}
        </div>
      </div>
      {confirmationModalDetails ? (
        <WModal
          onClose={() => setConfirmationModalDetails(null)}
          isOpen={Boolean(confirmationModalDetails)}
          title={'Re-populate Discography IDs?'}
        >
          <div className="tw-flex tw-flex-col">
            <div>
              Are you sure you want to continue?
              <br />
              This will overwrite the existing populated IDs
            </div>
            <div className="tw-mt-4 tw-w-full tw-flex tw-justify-between">
              <WButton
                label="Cancel"
                variant="secondary"
                onClick={() => {
                  setConfirmationModalDetails(null);
                }}
              />
              <WButton
                label="Continue"
                variant="primary"
                onClick={() => {
                  autoCollectIds(
                    confirmationModalDetails.clientId,
                    confirmationModalDetails.sourceId,
                    confirmationModalDetails.source
                  );
                  setConfirmationModalDetails(null);
                }}
              />
            </div>
          </div>
        </WModal>
      ) : null}
    </RateLimiterProvider>
  );
}
